import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { Eye, EyeOff, Loader2, MailCheck } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getSubscription } from '@/services/subscription'

const loginSchema = z.object({
  email: z.string().email({ message: 'Email inválido.' }),
  password: z.string().min(1, { message: 'Senha é obrigatória.' }),
})

type LoginFormValues = z.infer<typeof loginSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { signIn } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (searchParams.get('confirmed') === 'true') {
      setShowConfirmation(true)
    }
  }, [searchParams])

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true)
    const { error } = await signIn(data.email, data.password)

    if (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao acessar',
        description:
          error.message === 'Invalid login credentials'
            ? 'Email ou senha inválidos. Tente novamente.'
            : error.message,
      })
      setIsLoading(false)
    } else {
      toast({
        title: 'Login realizado com sucesso!',
        description: 'Verificando sua assinatura...',
      })
      try {
        const subscription = await getSubscription()
        if (subscription) {
          navigate('/dashboard')
        } else {
          navigate('/subscribe')
        }
      } catch (subError) {
        toast({
          variant: 'destructive',
          title: 'Erro ao verificar assinatura',
          description: 'Redirecionando para a página de assinatura.',
        })
        navigate('/subscribe')
      }
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Acessar sua Conta</CardTitle>
          <CardDescription className="text-muted-foreground">
            Bem-vindo de volta!
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showConfirmation && (
            <Alert className="mb-4 border-green-500/70 text-green-400 [&>svg]:text-green-400">
              <MailCheck className="h-4 w-4" />
              <AlertTitle>Email Confirmado!</AlertTitle>
              <AlertDescription>
                Seu email foi verificado com sucesso. Agora você pode fazer
                login.
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                {...form.register('email')}
              />
              {form.formState.errors.email && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>
            <div className="space-y-2 relative">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Sua senha"
                {...form.register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {form.formState.errors.password && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                'Acessar'
              )}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Não tem uma conta?{' '}
            <Link
              to="/signup"
              className="font-medium text-primary hover:underline"
            >
              Criar Conta
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
